/**
 * Auto-Match Dialog: Generic, direction-agnostic field matching preview.
 *
 * Reused by both Input and Output mapping tabs. The dialog is purely
 * presentational — it computes suggestions via `autoMatchFields` and
 * returns the selected `AutoMatchSuggestion[]` to the caller. Each tab
 * converts suggestions to its own mapping type (SOLID: single responsibility).
 *
 * A row is one field of the given side with the field proposed for it. The
 * proposed field is a select (border, chevron inside) when other candidates
 * exist; the given field is plain. By default the targets are given and
 * their source is chosen; `choiceSide="target"` turns that round: the
 * sources are given and their target is chosen.
 */

import {
  Button,
  Checkbox,
  Menu,
  MenuItem,
  Modal,
  notify,
} from '@affine/component';
import { useI18n } from '@affine/i18n';
import {
  autoMatchFields,
  type AutoMatchSuggestion,
  checkCompatibility,
  type MatchConfidence,
  normalizeType,
  rateFieldMatch,
  type ResponseFieldInfo,
  type TargetColumnInfo,
} from '@streamlain/field-types';
import type { FC, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { DropdownChevron } from '../../shared/dropdown-chevron';
import * as styles from './auto-match-dialog.css';
import { MappingCell } from './mapping-cell';
import { mappingPairSide } from './section-layout.css';
import { CompatDot, TypeChip } from './type-chip';
import { mappingTriggerStacked } from './typed-field-select.css';

// =============================================================================
// Props
// =============================================================================

interface AutoMatchDialogProps {
  open: boolean;
  onClose: () => void;
  sourceFields: ResponseFieldInfo[];
  targetFields: TargetColumnInfo[];
  existingTargetIds?: Set<string>;
  isMappingAllowed?: (
    source: ResponseFieldInfo,
    target: TargetColumnInfo
  ) => boolean;
  sourceLabel?: string;
  targetLabel?: string;
  /**
   * The side that belongs to the workflow; the other one is a table. Left out
   * where no workflow takes part (Marketplace import): no side is marked.
   */
  workflowSide?: 'source' | 'target';
  /**
   * The side whose field is chosen per row; the other side's fields are the
   * rows. Left out: the source (each target gets the source that fits best).
   */
  choiceSide?: 'source' | 'target';
  onApply: (suggestions: AutoMatchSuggestion[]) => void;
}

// =============================================================================
// Constants
// =============================================================================

const CONFIDENCE_COLORS: Record<string, string> = {
  high: '#4CAF50',
  medium: '#FF9800',
  low: '#9ca3af',
};

// Labels resolved via i18n at render time — see ConfidenceBadge
const CONFIDENCE_ORDER: Record<MatchConfidence, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

/** A field that can take the place of a row's proposed one. */
interface Choice {
  id: string;
  name: string;
  /** The pair the row would be with this choice. */
  sourceType: string;
  targetType: string;
  confidence: MatchConfidence;
  selected: boolean;
  /** Another row already takes it (a target gets one value). */
  taken: boolean;
}

const CONFIDENCE_KEYS: Record<string, string> = {
  high: 'com.streamlain.streamlain.automap.confidence-high',
  medium: 'com.streamlain.streamlain.automap.confidence-medium',
  low: 'com.streamlain.streamlain.automap.confidence-low',
};

/**
 * Generate a specific tooltip for a source→target type cast.
 */
function castTooltip(sourceType: string, targetType: string): string {
  const s = normalizeType(sourceType);
  const t = normalizeType(targetType);
  if (s === t)
    return `${sourceType} → ${targetType}: Compatible — no conversion needed`;

  // Specific cast descriptions
  if (s === 'select' && t === 'text')
    return 'Select → Text: Option label as string';
  if (s === 'multi-select' && t === 'text')
    return 'Multi-Select → Text: Labels joined with comma';
  if (s === 'number' && t === 'text') return 'Number → Text: Numeric string';
  if (s === 'date' && t === 'text') return 'Date → Text: ISO date string';
  if (s === 'checkbox' && t === 'text')
    return 'Checkbox → Text: "true" or "false"';
  if (s === 'rich-text' && t === 'text')
    return '⚠ Rich-Text → Text: Markup/formatting is stripped';
  if (s === 'attachment' && t === 'text')
    return '⚠ Attachment → Text: Only filename — file data is lost';
  if (s === 'relation' && t === 'text')
    return '⚠ Relation → Text: Outputs relation ID. Can reconstruct link in a target relation column if configured correctly — otherwise renders as raw ID';
  if (s === 'text' && t === 'relation')
    return '⚠ Text → Relation: High risk — only works if the value is a valid relation ID and the target column accepts it';
  if (s === 'text' && t === 'rich-text')
    return 'Text → Rich-Text: Plain text wrapped in paragraph';
  if (
    (s === 'select' && t === 'multi-select') ||
    (s === 'multi-select' && t === 'select')
  )
    return `${sourceType} ↔ ${targetType}: Semantically related — single/multi conversion`;
  if (t === 'json') return `${sourceType} → JSON: Value serialized as JSON`;
  if (s === 'json') return `JSON → ${targetType}: Value extracted from JSON`;

  const compat = checkCompatibility(targetType, sourceType);
  if (compat === 'castable')
    return `${sourceType} → ${targetType}: Auto-cast — may lose precision`;
  return `${sourceType} → ${targetType}: Types may not be compatible`;
}

/** Shared confidence badge — DRY across main row and dropdown. */
function ConfidenceBadge({
  confidence,
  t,
}: {
  confidence: string;
  t: ReturnType<typeof useI18n>;
}) {
  const key = CONFIDENCE_KEYS[confidence];
  const label = key
    ? ((t[key as keyof typeof t] as (() => string) | undefined)?.() ??
      confidence)
    : confidence;
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: '10px',
        backgroundColor: CONFIDENCE_COLORS[confidence] ?? '#9ca3af',
        color: 'white',
        minWidth: '52px',
        textAlign: 'center',
        display: 'inline-block',
        flexShrink: 0,
        lineHeight: '16px',
        boxSizing: 'border-box',
      }}
    >
      {label}
    </span>
  );
}

// =============================================================================
// Component
// =============================================================================

export const AutoMatchDialog: FC<AutoMatchDialogProps> = ({
  open,
  onClose,
  sourceFields,
  targetFields,
  existingTargetIds,
  isMappingAllowed,
  sourceLabel = 'Source',
  targetLabel = 'Target',
  workflowSide,
  choiceSide = 'source',
  onApply,
}) => {
  const t = useI18n();
  /** A row is identified by its given field. */
  const rowKeyOf = useCallback(
    (suggestion: AutoMatchSuggestion) =>
      choiceSide === 'source'
        ? suggestion.targetColumnId
        : suggestion.responseField,
    [choiceSide]
  );

  const initialSuggestions = useMemo(() => {
    return autoMatchFields(
      sourceFields,
      targetFields,
      existingTargetIds ?? new Set(),
      isMappingAllowed
    );
  }, [sourceFields, targetFields, existingTargetIds, isMappingAllowed]);

  // Mutable suggestions state — user can switch alternatives via dropdown
  const [suggestions, setSuggestions] =
    useState<AutoMatchSuggestion[]>(initialSuggestions);

  const [enabled, setEnabled] = useState<Set<string>>(
    () => new Set(initialSuggestions.map(rowKeyOf))
  );

  // Reset when initial suggestions change (dialog reopened)
  useMemo(() => {
    setSuggestions(initialSuggestions);
    setEnabled(new Set(initialSuggestions.map(rowKeyOf)));
  }, [initialSuggestions, rowKeyOf]);

  const toggleSuggestion = useCallback((rowKey: string) => {
    setEnabled(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (enabled.size === suggestions.length) {
      setEnabled(new Set());
    } else {
      setEnabled(new Set(suggestions.map(rowKeyOf)));
    }
  }, [enabled.size, rowKeyOf, suggestions]);

  const enabledCount = useMemo(
    () => suggestions.filter(s => enabled.has(rowKeyOf(s))).length,
    [suggestions, enabled, rowKeyOf]
  );

  /** Puts another field in the place of a row's proposed one. */
  const switchChoice = useCallback(
    (rowKey: string, choice: Choice) => {
      setSuggestions(prev =>
        prev.map(s => {
          if (rowKeyOf(s) !== rowKey) return s;
          return choiceSide === 'source'
            ? {
                ...s,
                responseField: choice.id,
                responseFieldName: choice.name,
                confidence: choice.confidence,
              }
            : {
                ...s,
                targetColumnId: choice.id,
                targetColumnName: choice.name,
                confidence: choice.confidence,
              };
        })
      );
    },
    [choiceSide, rowKeyOf]
  );

  const handleApply = useCallback(() => {
    const selected = suggestions.filter(s => enabled.has(rowKeyOf(s)));
    onApply(selected);
    notify.success({
      title: t['com.streamlain.streamlain.automap.success']({
        count: String(selected.length),
      }),
    });
    onClose();
  }, [suggestions, enabled, onApply, onClose, rowKeyOf, t]);

  // Type lookup
  const sourceTypeMap = useMemo(
    () => new Map(sourceFields.map(f => [f.path, f.type])),
    [sourceFields]
  );
  const targetTypeMap = useMemo(
    () => new Map(targetFields.map(c => [c.id, c.type])),
    [targetFields]
  );

  /** Given fields that got no proposal, by name. */
  const unmatched = useMemo(() => {
    if (choiceSide === 'source') {
      const matched = new Set(suggestions.map(s => s.targetColumnId));
      return targetFields.filter(c => !matched.has(c.id)).map(c => c.name);
    }
    const matched = new Set(suggestions.map(s => s.responseField));
    return sourceFields
      .filter(f => !matched.has(f.path))
      .map(f => f.displayName || f.path);
  }, [choiceSide, suggestions, sourceFields, targetFields]);

  /** The fields that can stand in a row's chosen place, best fit first. */
  const choicesOf = useCallback(
    (suggestion: AutoMatchSuggestion): Choice[] => {
      if (choiceSide === 'source') {
        const targetType =
          targetTypeMap.get(suggestion.targetColumnId) ?? 'text';
        return suggestion.alternatives.map(alternative => ({
          id: alternative.responseField,
          name: alternative.responseFieldName || alternative.responseField,
          sourceType: sourceTypeMap.get(alternative.responseField) ?? 'text',
          targetType,
          confidence: alternative.confidence,
          selected: alternative.responseField === suggestion.responseField,
          taken: false,
        }));
      }
      const source = sourceFields.find(
        field => field.path === suggestion.responseField
      );
      if (!source) return [];
      const takenElsewhere = new Set(
        suggestions
          .filter(other => other !== suggestion)
          .map(other => other.targetColumnId)
      );
      return targetFields
        .flatMap(target => {
          if (isMappingAllowed && !isMappingAllowed(source, target)) return [];
          const confidence = rateFieldMatch(source, target);
          return confidence
            ? [
                {
                  id: target.id,
                  name: target.name,
                  sourceType: source.type,
                  targetType: target.type,
                  confidence,
                  selected: target.id === suggestion.targetColumnId,
                  taken: takenElsewhere.has(target.id),
                },
              ]
            : [];
        })
        .sort(
          (a, b) =>
            CONFIDENCE_ORDER[a.confidence] - CONFIDENCE_ORDER[b.confidence]
        );
    },
    [
      choiceSide,
      isMappingAllowed,
      sourceFields,
      sourceTypeMap,
      suggestions,
      targetFields,
      targetTypeMap,
    ]
  );

  return (
    <Modal
      open={open}
      onOpenChange={v => {
        if (!v) onClose();
      }}
      title={t['com.streamlain.streamlain.automap.title']()}
      width={560}
    >
      <div
        style={{
          padding: '0 24px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {/* Explanation */}
        <div
          style={{
            padding: '12px',
            fontSize: '13px',
            lineHeight: 1.5,
            backgroundColor: 'var(--streamlain-v2-layer-background-secondary)',
            borderRadius: '8px',
          }}
        >
          {t['com.streamlain.streamlain.automap.description']()}
        </div>

        {suggestions.length === 0 ? (
          <div
            style={{
              padding: '24px',
              textAlign: 'center',
              fontSize: '13px',
              opacity: 0.6,
            }}
          >
            {t['com.streamlain.streamlain.automap.no-matches']()}
          </div>
        ) : (
          <>
            {/* Head: the same columns as the rows. */}
            <div className={styles.head} data-testid="auto-match-head">
              <div className={styles.center}>
                <Checkbox
                  checked={enabled.size === suggestions.length}
                  onChange={() => toggleAll()}
                />
              </div>
              <span
                className={styles.side}
                data-workflow={workflowSide === 'source' || undefined}
              >
                {sourceLabel}
              </span>
              <span aria-hidden="true" />
              <span
                className={styles.side}
                data-workflow={workflowSide === 'target' || undefined}
              >
                {targetLabel}
              </span>
              <span className={styles.side} data-end>
                {t['com.streamlain.streamlain.automap.match']()}
              </span>
            </div>

            {/* Suggestion rows */}
            <div className={styles.rows}>
              {suggestions.map(s => {
                const rowKey = rowKeyOf(s);
                const sourceType = sourceTypeMap.get(s.responseField) ?? 'text';
                const targetType =
                  targetTypeMap.get(s.targetColumnId) ?? 'text';
                const choices = choicesOf(s);

                /**
                 * One side of the row. The chosen side is a select when
                 * there is something to choose from; a given field is plain.
                 */
                const side = (
                  name: 'source' | 'target',
                  cell: ReactNode,
                  label: string
                ) =>
                  name === choiceSide && choices.length > 1 ? (
                    <Menu
                      items={choices.map(choice => (
                        <MenuItem
                          key={choice.id}
                          selected={choice.selected}
                          disabled={choice.taken}
                          onClick={() => switchChoice(rowKey, choice)}
                        >
                          <div className={styles.choiceOption}>
                            <span className={styles.choiceName}>
                              {choice.name}
                            </span>
                            <TypeChip
                              type={
                                name === 'source'
                                  ? choice.sourceType
                                  : choice.targetType
                              }
                            />
                            <ConfidenceBadge
                              confidence={choice.confidence}
                              t={t}
                            />
                            <CompatDot
                              compatibility={checkCompatibility(
                                choice.targetType,
                                choice.sourceType
                              )}
                              tooltip={castTooltip(
                                choice.sourceType,
                                choice.targetType
                              )}
                            />
                          </div>
                        </MenuItem>
                      ))}
                      contentOptions={{
                        side: 'bottom',
                        align: 'start',
                        sideOffset: 4,
                      }}
                    >
                      <button
                        type="button"
                        className={mappingTriggerStacked}
                        data-side={name}
                        aria-label={label}
                        data-testid="auto-match-choice"
                      >
                        {cell}
                        <DropdownChevron />
                      </button>
                    </Menu>
                  ) : (
                    <div
                      className={mappingPairSide}
                      data-side={name}
                      data-fixed
                    >
                      {cell}
                    </div>
                  );

                return (
                  <div
                    key={rowKey}
                    className={styles.row}
                    data-off={!enabled.has(rowKey) || undefined}
                    data-testid="auto-match-row"
                  >
                    <div className={styles.center}>
                      <Checkbox
                        checked={enabled.has(rowKey)}
                        onChange={() => toggleSuggestion(rowKey)}
                      />
                    </div>

                    {side(
                      'source',
                      <MappingCell
                        name={s.responseFieldName || s.responseField}
                        type={sourceType}
                      />,
                      sourceLabel
                    )}

                    <div className={styles.center}>
                      <CompatDot
                        compatibility={checkCompatibility(
                          targetType,
                          sourceType
                        )}
                        tooltip={castTooltip(sourceType, targetType)}
                      />
                    </div>

                    {side(
                      'target',
                      <MappingCell
                        name={s.targetColumnName}
                        type={targetType}
                      />,
                      targetLabel
                    )}

                    <ConfidenceBadge confidence={s.confidence} t={t} />
                  </div>
                );
              })}
            </div>

            {/* Unmatched info */}
            {unmatched.length > 0 && (
              <div
                className={styles.unmatched}
                data-testid="auto-match-unmatched"
              >
                {t[
                  unmatched.length === 1
                    ? 'com.streamlain.streamlain.automap.unmatched'
                    : 'com.streamlain.streamlain.automap.unmatched_plural'
                ]({ count: String(unmatched.length) })}{' '}
                {unmatched.slice(0, 5).join(', ')}
                {unmatched.length > 5 &&
                  t['com.streamlain.streamlain.automap.unmatched-more']({
                    count: String(unmatched.length - 5),
                  })}
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            paddingTop: '8px',
            borderTop:
              '1px solid var(--streamlain-v2-layer-insideBorder-border)',
          }}
        >
          <Button onClick={onClose}>
            {t['com.streamlain.streamlain.automap.cancel']()}
          </Button>
          <Button
            variant="primary"
            onClick={handleApply}
            disabled={enabledCount === 0}
          >
            {t[
              enabledCount === 1
                ? 'com.streamlain.streamlain.automap.apply'
                : 'com.streamlain.streamlain.automap.apply_plural'
            ]({ count: String(enabledCount) })}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
